'use client';

import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { Icon } from '@benzinga/core-ui';
import type { Section, SectionLink, PageContext } from '../../entities';
import { resolveRelatedLinks, type ResolvedRelatedLinks } from '../../related-links-data';
import { RelatedLinks } from './RelatedLinks';
import styles from './SiteNav.module.scss';

export interface SectionMenuProps {
  section: Section;
  pageContext?: PageContext;
  onLinkClick?: (section: Section, link: SectionLink) => void;
}

const GENERIC_CONTEXT: PageContext = { kind: 'generic' };

export const SectionMenu = ({ section, pageContext = GENERIC_CONTEXT, onLinkClick }: SectionMenuProps) => {
  const related: ResolvedRelatedLinks = resolveRelatedLinks(section.key, pageContext) ?? {
    heading: section.related.heading,
    description: section.related.description,
    links: section.related.links.map(link => ({ label: link.label, href: link.href, hub: link.accent ?? 'brand' })),
  };

  const landing: SectionLink | null = section.href ? { label: section.label, href: section.href } : null;

  return (
    <div className="flex items-stretch max-[1080px]:flex-col max-[1080px]:gap-2 max-[1080px]:px-0 max-[1080px]:py-3">
      {landing ? (
        <div className={`${styles.landing} flex w-[232px] flex-none flex-col px-6 pb-6 pt-[22px] text-[var(--hubnav-util-fg)] max-[1080px]:mx-3 max-[1080px]:w-auto max-[1080px]:flex-row max-[1080px]:items-center max-[1080px]:gap-3.5 max-[1080px]:rounded-xl max-[1080px]:px-3 max-[1080px]:py-3 max-[1080px]:pl-4 max-[359px]:flex-col max-[359px]:items-start`}>
          <p className="m-0 text-xl font-extrabold leading-[1.15] tracking-[-0.02em] text-[var(--hubnav-util-sym)] text-balance max-[1080px]:hidden">
            {section.label}
          </p>
          {section.blurb ? (
            <p className="mb-0 mt-2.5 text-[13px] leading-normal text-pretty max-[1080px]:m-0 max-[1080px]:flex-1">
              {section.blurb}
            </p>
          ) : null}
          <span className="mt-auto h-8 max-[1080px]:hidden" aria-hidden="true" />
          <a
            className="group inline-flex items-center gap-2 self-start rounded-lg bg-[var(--hubnav-accent)] px-3.5 py-[9px] text-[13px] font-bold text-white no-underline transition-colors duration-100 ease-out hover:bg-[var(--hubnav-accent-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--hubnav-util-sym)] max-[1080px]:min-h-10 max-[1080px]:flex-none max-[1080px]:self-center max-[359px]:self-start"
            href={landing.href}
            onClick={() => onLinkClick?.(section, landing)}
          >
            Go to {section.shortLabel ?? section.label}
            <Icon
              className="h-[11px] w-[11px] transition-transform duration-150 ease-out group-hover:translate-x-0.5 motion-reduce:transition-none"
              icon={faArrowRight}
            />
          </a>
        </div>
      ) : null}
      <div className="grid grid-cols-[repeat(3,max-content)] content-start gap-x-8 gap-y-2 px-4 pb-[22px] pt-5 max-[1080px]:grid-cols-2 max-[1080px]:gap-3 max-[1080px]:px-2.5 max-[1080px]:pb-0 max-[1080px]:pt-1.5 max-[359px]:grid-cols-1">
        {section.columns.map(column => (
          <div className="max-w-[240px] max-[1080px]:max-w-none" key={column.heading}>
            <h4 className="mb-1.5 ml-2.5 mr-2.5 mt-0 border-b border-[var(--hubnav-line)] py-1 text-[11px] font-bold uppercase leading-tight tracking-[0.06em] text-[var(--hubnav-muted)]">
              {column.heading}
            </h4>
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {column.links.map(link => (
                <li key={`${link.href}-${link.label}`}>
                  <a
                    className="flex min-h-9 flex-col justify-center rounded-lg px-2.5 py-1.5 text-sm font-semibold leading-tight text-[var(--hubnav-fg)] no-underline transition-colors duration-100 ease-out hover:bg-[var(--hubnav-hover)] hover:text-[var(--hubnav-hover-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--hubnav-accent)] max-[1080px]:min-h-11"
                    href={link.href}
                    onClick={() => onLinkClick?.(section, link)}
                  >
                    {link.label}
                    {link.note ? (
                      <span className="mt-0.5 text-xs font-normal text-[var(--hubnav-muted)]">{link.note}</span>
                    ) : null}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="m-3 ml-2 w-[248px] flex-none self-start rounded-[10px] bg-[var(--hubnav-related-bg)] px-2 pb-2 pt-3 max-[1080px]:mx-3 max-[1080px]:mb-0 max-[1080px]:mt-1 max-[1080px]:w-auto max-[1080px]:self-stretch">
        <RelatedLinks
          heading={related.heading}
          description={related.description}
          entityLabel={related.entityLabel}
          links={related.links}
          onLinkClick={link => onLinkClick?.(section, { label: link.label, href: link.href })}
        />
      </div>
    </div>
  );
};
